import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AuditCase, AuditFinding, AuditFindingAnalytics, AuditFindingEscalationRule, AuditFindingFilters, SelectOption } from '../audit.models';
import { AuditService } from '../audit.service';
import {
  auditNavItems,
  booleanFilterOptions,
  escalationLevelOptions,
  escalationStatusOptions,
  findingStatusOptions,
  findingTypeOptions,
  formatAuditDate,
  impactScopeOptions,
  moduleOptions,
  severityOptions,
  statusSeverity
} from '../audit-ui';

@Component({
  selector: 'app-audit-findings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Audit Execution" title="Audit Findings" subtitle="Nonconformities, observations, and auto-created failed-control findings.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="item.route !== '/audit/findings'" severity="secondary"></a>
          }
        </div>
        <div actions>
          <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" (onClick)="load()"></p-button>
          <p-button label="Escalation Rules" icon="pi pi-bell" [outlined]="true" severity="secondary" (onClick)="openRules()" [disabled]="!canManageSettings"></p-button>
          <p-button label="New Finding" icon="pi pi-plus" (onClick)="openFinding()" [disabled]="!canManage"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-data-panel title="Finding Filters" subtitle="Narrow by category, severity, owner, repeat status, and source." [resultText]="activeFilterCount + ' active filters'">
        <div class="audit-form-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-info-circle"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="filter-status" [options]="statusFilterOptions" [(ngModel)]="filters.status" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="filter-status">Status</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-tags"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="filter-type" [options]="findingTypeOptions" [(ngModel)]="filters.findingType" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="filter-type">Finding Type</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="filter-severity" [options]="severityOptions" [(ngModel)]="filters.severity" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="filter-severity">Severity</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-inputNumber inputId="filter-owner" [(ngModel)]="filters.ownerUserId" [useGrouping]="false"></p-inputNumber>
              <label for="filter-owner">Owner User ID</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-refresh"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="filter-repeat" [options]="booleanFilterOptions" [(ngModel)]="filters.repeatFinding" optionLabel="label" optionValue="value"></p-select>
              <label for="filter-repeat">Repeat Finding</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-wallet"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="filter-financial-impact" [options]="booleanFilterOptions" [(ngModel)]="filters.hasFinancialImpact" optionLabel="label" optionValue="value"></p-select>
              <label for="filter-financial-impact">Financial Impact</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="filter-module" [options]="moduleOptions" [(ngModel)]="filters.sourceModule" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="filter-module">Source Module</label>
            </p-iftalabel>
          </p-inputgroup>
          <div class="filter-actions">
            <p-button label="Apply" icon="pi pi-search" (onClick)="load()"></p-button>
            <p-button label="Clear" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="clearFilters()"></p-button>
          </div>
        </div>
      </app-page-data-panel>

      <app-page-data-panel title="Findings" subtitle="Finding numbers, severity, status, owners, and due dates." [resultText]="findings.length + ' findings'">
        <p-table [value]="findings" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1120px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Finding</th>
              <th>Type</th>
              <th>Case</th>
              <th>Source</th>
              <th>Severity</th>
              <th>Status</th>
              <th>SLA / Due</th>
              <th>Repeat</th>
              <th class="text-right">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-finding>
            <tr>
              <td>
                <div class="audit-stack">
                  <a [routerLink]="['/audit/findings', finding.findingId]"><strong>{{ finding.title }}</strong></a>
                  <small>{{ finding.findingNumber || '-' }}</small>
                </div>
              </td>
              <td>{{ finding.findingType || '-' }}</td>
              <td><a [routerLink]="['/audit/cases', finding.auditCaseId]">Case #{{ finding.auditCaseId }}</a></td>
              <td>
                <div class="audit-stack">
                  <span>{{ finding.sourceResourceNumber || '-' }}</span>
                  <small>{{ finding.sourceModule || finding.sourceResourceType || '-' }}</small>
                </div>
              </td>
              <td><p-tag [value]="finding.severity || '-'" [severity]="statusSeverity(finding.severity)"></p-tag></td>
              <td><p-tag [value]="finding.status || '-'" [severity]="statusSeverity(finding.status)"></p-tag></td>
              <td>
                <div class="audit-stack">
                  <span>{{ formatDate(finding.slaDueAt || finding.dueAt) }}</span>
                  @if (finding.overdue || finding.slaBreached) {
                    <small class="danger-text">{{ finding.daysOverdue || 1 }} days overdue</small>
                  }
                </div>
              </td>
              <td>
                @if (finding.repeatFinding) {
                  <p-tag value="Repeat" severity="warn"></p-tag>
                } @else {
                  <span>-</span>
                }
              </td>
              <td class="text-right">
                <a pButton [routerLink]="['/audit/findings', finding.findingId]" icon="pi pi-arrow-right" [text]="true" pTooltip="Open finding"></a>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="9" class="text-center p-4">No audit findings are available.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>

    <p-dialog header="New Finding" [(visible)]="dialogVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(760px, 96vw)' }">
      <div class="audit-form-grid">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-briefcase"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="finding-case" [options]="caseOptions" [(ngModel)]="form.auditCaseId" optionLabel="label" optionValue="value"></p-select>
            <label for="finding-case">Audit case</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="finding-severity" [options]="severityOptions" [(ngModel)]="form.severity" optionLabel="label" optionValue="value"></p-select>
            <label for="finding-severity">Severity</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-tags"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="finding-type" [options]="findingTypeOptions" [(ngModel)]="form.findingType" optionLabel="label" optionValue="value"></p-select>
            <label for="finding-type">Finding Type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="finding-risk" [options]="severityOptions" [(ngModel)]="form.riskRating" optionLabel="label" optionValue="value"></p-select>
            <label for="finding-risk">Risk Rating</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="finding-source-module" [options]="moduleOptions" [(ngModel)]="form.sourceModule" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
            <label for="finding-source-module">Source Module</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-compass"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="finding-impact-scope" [options]="impactScopeOptions" [(ngModel)]="form.impactScope" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
            <label for="finding-impact-scope">Impact Scope</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-inputNumber inputId="finding-owner" [(ngModel)]="form.ownerUserId" [useGrouping]="false"></p-inputNumber>
            <label for="finding-owner">Owner User ID</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-calendar-clock"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="finding-due" type="datetime-local" [(ngModel)]="dueAtInput">
            <label for="finding-due">Due Date</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-stopwatch"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="finding-sla-due" type="datetime-local" [(ngModel)]="slaDueAtInput">
            <label for="finding-sla-due">SLA Due</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-wallet"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-inputNumber inputId="finding-financial-impact" [(ngModel)]="form.financialImpactAmount" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2"></p-inputNumber>
            <label for="finding-financial-impact">Financial Impact</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-heading"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="finding-title" [(ngModel)]="form.title">
            <label for="finding-title">Title</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="finding-description" rows="4" [(ngModel)]="form.description"></textarea>
            <label for="finding-description">Description</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="finding-root-cause" rows="3" [(ngModel)]="form.rootCause"></textarea>
            <label for="finding-root-cause">Root Cause</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-chart-line"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="finding-operational-impact" rows="3" [(ngModel)]="form.operationalImpact"></textarea>
            <label for="finding-operational-impact">Operational Impact</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-users"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="finding-customer-impact" rows="3" [(ngModel)]="form.customerImpact"></textarea>
            <label for="finding-customer-impact">Customer Impact</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="finding-carrier-impact" rows="3" [(ngModel)]="form.carrierImpact"></textarea>
            <label for="finding-carrier-impact">Carrier Impact</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (formError) {
        <div class="audit-error">{{ formError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="dialogVisible = false"></p-button>
        <p-button label="Save Finding" icon="pi pi-check" (onClick)="saveFinding()" [loading]="saving" [disabled]="!canManage"></p-button>
      </ng-template>
    </p-dialog>

    <p-dialog header="Finding Escalation Rules" [(visible)]="rulesVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(980px, 96vw)' }">
      <div class="rules-layout">
        <p-table [value]="escalationRules" styleClass="p-datatable-sm" responsiveLayout="scroll">
          <ng-template pTemplate="header">
            <tr><th>Rule</th><th>Match</th><th>SLA</th><th>Escalation</th><th>Status</th><th></th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-rule>
            <tr>
              <td>
                <div class="audit-stack">
                  <strong>{{ rule.ruleName }}</strong>
                  <small>{{ rule.ruleCode }}</small>
                </div>
              </td>
              <td>{{ rule.findingType || 'Any type' }} / {{ rule.severity || 'Any severity' }}</td>
              <td>{{ rule.slaDays || '-' }} days</td>
              <td>{{ rule.escalationAfterDays || '-' }} days / {{ rule.escalationLevel || '-' }}</td>
              <td><p-tag [value]="rule.active ? 'ACTIVE' : 'INACTIVE'" [severity]="rule.active ? 'success' : 'secondary'"></p-tag></td>
              <td class="text-right"><p-button icon="pi pi-pencil" [text]="true" (onClick)="editRule(rule)"></p-button></td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="6" class="text-center p-4">No escalation rules are configured.</td></tr>
          </ng-template>
        </p-table>

        <div class="audit-form-grid rule-form">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-code"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="rule-code" [(ngModel)]="ruleForm.ruleCode">
              <label for="rule-code">Rule Code</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-heading"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="rule-name" [(ngModel)]="ruleForm.ruleName">
              <label for="rule-name">Rule Name</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-tags"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="rule-type" [options]="findingTypeOptions" [(ngModel)]="ruleForm.findingType" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="rule-type">Finding Type</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="rule-severity" [options]="severityOptions" [(ngModel)]="ruleForm.severity" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="rule-severity">Severity</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-stopwatch"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-inputNumber inputId="rule-sla" [(ngModel)]="ruleForm.slaDays" [useGrouping]="false" [min]="0"></p-inputNumber>
              <label for="rule-sla">SLA Days</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-bell"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-inputNumber inputId="rule-escalate-after" [(ngModel)]="ruleForm.escalationAfterDays" [useGrouping]="false" [min]="0"></p-inputNumber>
              <label for="rule-escalate-after">Escalate After Days</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-angle-double-up"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="rule-level" [options]="escalationLevelOptions" [(ngModel)]="ruleForm.escalationLevel" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="rule-level">Escalation Level</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-inputNumber inputId="rule-owner" [(ngModel)]="ruleForm.escalationOwnerUserId" [useGrouping]="false"></p-inputNumber>
              <label for="rule-owner">Escalation Owner User ID</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>
      </div>
      @if (ruleError) {
        <div class="audit-error">{{ ruleError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="New Rule" icon="pi pi-plus" severity="secondary" [outlined]="true" (onClick)="newRule()"></p-button>
        <p-button label="Close" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="rulesVisible = false"></p-button>
        <p-button label="Save Rule" icon="pi pi-check" (onClick)="saveRule()" [loading]="saving" [disabled]="!canManageSettings"></p-button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .audit-stack {
      display: flex;
      flex-direction: column;
      gap: .25rem;
      min-width: 0;
    }

    .audit-stack small {
      color: #64748b;
      font-size: .78rem;
    }

    .audit-form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(180px, 1fr));
      gap: .85rem;
    }

    label {
      color: #334155;
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    label span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    input,
    textarea,
    p-iftalabel,
    p-inputgroup,
    p-select {
      width: 100%;
    }

    .wide-field {
      grid-column: 1 / -1;
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: .5rem;
      justify-content: flex-end;
    }

    .rules-layout {
      display: grid;
      gap: 1rem;
    }

    .rule-form {
      border-top: 1px solid #e2e8f0;
      padding-top: 1rem;
    }

    .danger-text {
      color: #b91c1c;
      font-weight: 800;
    }

    .audit-error {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #991b1b;
      font-weight: 700;
      margin-top: .85rem;
      padding: .75rem;
    }

    @media (max-width: 800px) {
      .audit-form-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AuditFindingsComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  saving = false;
  dialogVisible = false;
  rulesVisible = false;
  formError = '';
  ruleError = '';
  findings: AuditFinding[] = [];
  cases: AuditCase[] = [];
  analytics?: AuditFindingAnalytics;
  escalationRules: AuditFindingEscalationRule[] = [];
  filters: AuditFindingFilters = {};
  form: AuditFinding = {};
  ruleForm: AuditFindingEscalationRule = {};
  dueAtInput = '';
  slaDueAtInput = '';

  readonly severityOptions = severityOptions;
  readonly findingTypeOptions = findingTypeOptions;
  readonly statusFilterOptions = findingStatusOptions;
  readonly impactScopeOptions = impactScopeOptions;
  readonly moduleOptions = moduleOptions;
  readonly booleanFilterOptions = booleanFilterOptions;
  readonly escalationLevelOptions = escalationLevelOptions;
  readonly escalationStatusOptions = escalationStatusOptions;
  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly canManage = this.permissionService.hasPermission(app_permissions.audit.findings.manage);
  readonly canManageSettings = this.permissionService.hasPermission(app_permissions.audit.settings.manage);
  readonly statusSeverity = statusSeverity;
  readonly formatDate = formatAuditDate;

  ngOnInit(): void {
    this.load();
  }

  get caseOptions(): SelectOption<number>[] {
    return this.cases.map(item => ({ label: `${item.auditNumber || 'Case'} · ${item.status || '-'}`, value: item.auditCaseId! }));
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Findings', value: this.analytics?.totalFindings ?? this.findings.length, tone: 'info' },
      { label: 'Open', value: this.analytics?.openFindings ?? this.findings.filter(item => item.status !== 'CLOSED').length, tone: 'warn' },
      { label: 'Critical Open', value: this.analytics?.criticalOpenFindings ?? this.findings.filter(item => item.severity === 'CRITICAL' && item.status !== 'CLOSED').length, tone: 'danger' },
      { label: 'Repeat', value: this.analytics?.repeatFindings ?? this.findings.filter(item => item.repeatFinding).length, tone: 'warn' },
      { label: 'SLA Breached', value: this.analytics?.slaBreachedFindings ?? this.findings.filter(item => item.slaBreached).length, tone: 'danger' },
      { label: 'Financial Impact', value: this.analytics?.financialImpactFindings ?? this.findings.filter(item => item.financialImpactAmount != null).length }
    ];
  }

  get activeFilterCount(): number {
    return Object.values(this.filters).filter(value => value !== null && value !== undefined && value !== '').length;
  }

  load(): void {
    this.loading = true;
    forkJoin({
      findings: this.auditService.findings(this.filters),
      cases: this.auditService.cases(),
      analytics: this.auditService.findingAnalytics()
    }).subscribe({
      next: response => {
        this.findings = response.findings.data ?? [];
        this.cases = response.cases.data ?? [];
        this.analytics = response.analytics.data;
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Findings', detail: 'Unable to load audit findings.' }),
      complete: () => this.loading = false
    });
  }

  clearFilters(): void {
    this.filters = {};
    this.load();
  }

  openFinding(): void {
    this.formError = '';
    this.dueAtInput = '';
    this.slaDueAtInput = '';
    this.form = {
      severity: 'MEDIUM',
      riskRating: 'MEDIUM',
      status: 'OPEN',
      findingType: 'NONCONFORMITY',
      escalationStatus: 'OPEN'
    };
    this.dialogVisible = true;
  }

  saveFinding(): void {
    this.formError = '';
    if (!this.form.auditCaseId || !this.form.title?.trim()) {
      this.formError = 'Audit case and title are required.';
      return;
    }
    this.form.dueAt = this.dueAtInput ? new Date(this.dueAtInput).toISOString() : undefined;
    this.form.slaDueAt = this.slaDueAtInput ? new Date(this.slaDueAtInput).toISOString() : undefined;
    this.saving = true;
    this.auditService.createFinding(this.form).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Finding saved', detail: response.message || 'Audit finding saved.' });
        this.dialogVisible = false;
        this.load();
      },
      error: error => this.formError = error?.error?.message || 'Unable to save finding.',
      complete: () => this.saving = false
    });
  }

  openRules(): void {
    this.ruleError = '';
    this.rulesVisible = true;
    this.newRule();
    this.loadRules();
  }

  loadRules(): void {
    this.auditService.findingEscalationRules().subscribe({
      next: response => this.escalationRules = response.data ?? [],
      error: () => this.messageService.add({ severity: 'error', summary: 'Escalation rules', detail: 'Unable to load escalation rules.' })
    });
  }

  newRule(): void {
    this.ruleError = '';
    this.ruleForm = {
      active: true,
      escalationLevel: 'L1',
      slaDays: 7,
      escalationAfterDays: 5
    };
  }

  editRule(rule: AuditFindingEscalationRule): void {
    this.ruleError = '';
    this.ruleForm = { ...rule };
  }

  saveRule(): void {
    this.ruleError = '';
    if (!this.ruleForm.ruleCode?.trim() || !this.ruleForm.ruleName?.trim()) {
      this.ruleError = 'Rule code and name are required.';
      return;
    }
    this.saving = true;
    this.auditService.saveFindingEscalationRule(this.ruleForm).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Escalation rule saved', detail: response.message || 'Rule saved.' });
        this.newRule();
        this.loadRules();
      },
      error: error => this.ruleError = error?.error?.message || 'Unable to save escalation rule.',
      complete: () => this.saving = false
    });
  }
}
